import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { isEmailInvalid } from '../../helpers/general.helpers';
import { palette } from '../../theme';

export interface WelcomeTeamInviteDialogProps {
  open: boolean;
  teamName?: string;
  inviteCode: string | null;
  generatingCode: boolean;
  inviting: boolean;
  onGenerateCode: () => void;
  onCopyCode: () => void;
  onInvite: (emails: string[]) => void;
  onClose: () => void;
}

/** First-open prompt after a new team is created: team code and athlete invites. */
const WelcomeTeamInviteDialog: FC<WelcomeTeamInviteDialogProps> = ({
  open,
  teamName,
  inviteCode,
  generatingCode,
  inviting,
  onGenerateCode,
  onCopyCode,
  onInvite,
  onClose,
}) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [athleteEmail, setAthleteEmail] = useState('');
  const [athleteEmailError, setAthleteEmailError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const askedForCode = useRef(false);

  useEffect(() => {
    if (!open) {
      askedForCode.current = false;
      return;
    }
    if (inviteCode || generatingCode || askedForCode.current) return;
    askedForCode.current = true;
    onGenerateCode();
  }, [open, inviteCode, generatingCode, onGenerateCode]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (athleteEmail.length > 1 && isEmailInvalid(athleteEmail)) {
        setAthleteEmailError('This email is invalid. Please use a valid email');
      } else {
        setAthleteEmailError('');
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [athleteEmail]);

  const addAthlete = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (isEmailInvalid(athleteEmail)) {
      setAthleteEmailError('This email is invalid. Please use a valid email');
      return;
    }
    if (emails.includes(athleteEmail)) {
      setAthleteEmailError('This athlete is already added');
      return;
    }
    setAthleteEmailError('');
    setEmails((current) => [...current, athleteEmail]);
    setAthleteEmail('');
  };

  const sendInvites = () => {
    const next = [...emails];
    if (athleteEmail.length > 1) {
      if (isEmailInvalid(athleteEmail)) {
        setAthleteEmailError('This email is invalid. Please use a valid email');
        return;
      }
      if (!next.includes(athleteEmail)) next.push(athleteEmail);
    }
    if (next.length === 0) {
      setAthleteEmailError('Add at least one email');
      return;
    }
    setEmails([]);
    setAthleteEmail('');
    setAthleteEmailError('');
    onInvite(next);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { width: 480, maxWidth: 480, borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0.5 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Invite your athletes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {teamName ? `${teamName} is ready.` : 'Your team is ready.'} Share the code or send invites.
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
          Team code
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            border: `1px solid ${palette.divider}`,
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2.5,
            backgroundColor: '#f8fafc',
          }}
        >
          {generatingCode && !inviteCode ? (
            <CircularProgress size={22} />
          ) : inviteCode ? (
            <Typography variant="h5" fontWeight={800} letterSpacing={2} sx={{ fontFamily: 'monospace' }}>
              {inviteCode}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Code not ready yet
            </Typography>
          )}
          {inviteCode ? (
            <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={onCopyCode}>
              Copy
            </Button>
          ) : (
            <Button size="small" variant="contained" onClick={onGenerateCode} disabled={generatingCode}>
              Generate code
            </Button>
          )}
        </Box>

        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
          Invite by email
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            value={athleteEmail}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="name@example.com"
            onChange={(event) => setAthleteEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addAthlete();
              }
            }}
            error={athleteEmailError.length > 1}
          />
          <IconButton
            aria-label="add athlete"
            onClick={addAthlete}
            sx={{ bgcolor: palette.primary.light, color: palette.primary.main, borderRadius: '50%' }}
          >
            <AddIcon />
          </IconButton>
        </Box>
        <FormHelperText error={athleteEmailError.length > 1} sx={{ mt: 0.5, mx: 0 }}>
          {athleteEmailError.length > 1
            ? athleteEmailError
            : 'Add emails, then send. Athletes can also join with the team code.'}
        </FormHelperText>
        {emails.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            {emails.map((item) => (
              <Chip
                key={item}
                label={item}
                size="small"
                variant="outlined"
                onDelete={() => setEmails((current) => current.filter((email) => email !== item))}
              />
            ))}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Done
        </Button>
        <Button variant="contained" onClick={sendInvites} disabled={inviting}>
          {inviting ? <CircularProgress size={18} color="inherit" /> : 'Send invites'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeTeamInviteDialog;
